import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PlusCircle, Pencil, Trash2, ImageOff, ArrowLeft } from 'lucide-react'
import { getServices, deleteService } from '../api/services'
import { useAuth } from '../context/AuthContext'
import styles from './ServicesList.module.css'

export default function ServicesList() {
  const { token, user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/login', { replace: true })
    }
  }, [user, authLoading, navigate])

  useEffect(() => {
    getServices()
      .then(setServices)
      .catch(() => setError('Failed to load services.'))
      .finally(() => setLoading(false))
  }, [])

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete service "${name}"? This cannot be undone.`)) return
    setDeleting(id)
    try {
      await deleteService(token, id)
      setServices((prev) => prev.filter((s) => s.id !== id))
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete service.')
    } finally {
      setDeleting(null)
    }
  }

  if (authLoading || !user) return null

  return (
    <main className={styles.page}>
      <Link to="/dashboard" className={styles.back}>
        <ArrowLeft size={16} />
        Back to Dashboard
      </Link>

      <div className={styles.header}>
        <div>
          <h1 className={styles.heading}>Services</h1>
          <p className={styles.subtitle}>Manage the services you offer</p>
        </div>
        <Link to="/services-manage/new" className={styles.newBtn}>
          <PlusCircle size={16} />
          New Service
        </Link>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <p className={styles.loading}>Loading…</p>
      ) : services.length === 0 ? (
        <div className={styles.empty}>
          <p>No services yet.</p>
          <Link to="/services-manage/new" className={styles.emptyLink}>
            Add the first service →
          </Link>
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.thumbCell}></th>
                <th>Name</th>
                <th>Description</th>
                <th>Cost</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {services.map((s) => (
                <tr key={s.id}>
                  <td className={styles.thumbCell}>
                    {s.thumbnail ? (
                      <img src={s.thumbnail} alt={s.name} className={styles.thumb} />
                    ) : (
                      <span className={styles.thumbPlaceholder}>
                        <ImageOff size={18} />
                      </span>
                    )}
                  </td>
                  <td className={styles.name}>{s.name}</td>
                  <td className={styles.desc}>{s.description || '—'}</td>
                  <td className={styles.cost}>
                    {s.cost != null ? `$${Number(s.cost).toFixed(2)}` : '—'}
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      <Link
                        to={`/services-manage/${s.id}/edit`}
                        className={styles.iconBtn}
                        title="Edit service"
                      >
                        <Pencil size={14} />
                      </Link>
                      <button
                        className={`${styles.iconBtn} ${styles.deleteBtn}`}
                        onClick={() => handleDelete(s.id, s.name)}
                        disabled={deleting === s.id}
                        title="Delete service"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}
